import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Package, ArrowRightLeft, Inbox, History, ChevronRight,
  Check, X, Clock, AlertTriangle, PackageOpen, Send, User, CalendarDays,
} from "lucide-react";

/* ════════════════════════════════════════════════
   TechSupplies — Gestión de Insumos
   ════════════════════════════════════════════════ */
export default function TechSupplies() {
  const nav = useNavigate();
  const [tab, setTab] = useState("inventory"); // inventory | pending | history
  const [groups, setGroups] = useState([]);
  const [extras, setExtras] = useState([]);
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wzSource, setWzSource] = useState(null);       // supply obj
  const [wzQty, setWzQty] = useState("");
  const [wzDestType, setWzDestType] = useState("");      // "own" | "other"
  const [wzDestTechId, setWzDestTechId] = useState("");
  const [wzDestServiceId, setWzDestServiceId] = useState(""); // "" = extra
  const [wzDate, setWzDate] = useState(new Date().toISOString().slice(0, 10));
  const [wzNotes, setWzNotes] = useState("");
  const [technicians, setTechnicians] = useState([]);
  const [destServices, setDestServices] = useState([]);
  const [ownServices, setOwnServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [supRes, pendRes, histRes, countRes] = await Promise.all([
        api.get("/supplies/my"),
        api.get("/supplies/transfers/pending"),
        api.get("/supplies/transfers/history"),
        api.get("/supplies/transfers/pending-count"),
      ]);
      setGroups(supRes.data.groups || []);
      setExtras(supRes.data.extras || []);
      setPending(pendRes.data || []);
      setHistory(histRes.data || []);
      setPendingCount(countRes.data.count || 0);
    } catch {
      toast.error("Error al cargar insumos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load technicians for wizard
  const loadTechnicians = async () => {
    try {
      const res = await api.get("/supplies/technicians");
      setTechnicians(res.data.filter(t => t.full_name !== "Test Technician"));
    } catch { /* ignore */ }
  };

  // Load services for a technician
  const loadDestServices = async (techId) => {
    try {
      const res = await api.get(`/supplies/technician-services/${techId}`);
      setDestServices(res.data || []);
    } catch { setDestServices([]); }
  };

  // Load own services
  const loadOwnServices = async () => {
    try {
      // Use the current user's tech profile services
      const res = await api.get("/supplies/my");
      const svcIds = (res.data.groups || []).map(g => ({
        id: g.service_id,
        scheduled_date: g.service_date,
        scheduled_time: g.service_time,
        client_name: g.client_name,
        order_number: g.order_number,
      }));
      setOwnServices(svcIds);
    } catch { setOwnServices([]); }
  };

  // ─── Wizard helpers ──────────────────────────
  const openWizard = (supply) => {
    setWzSource(supply);
    setWzQty("");
    setWzDestType("");
    setWzDestTechId("");
    setWzDestServiceId("");
    setWzDate(new Date().toISOString().slice(0, 10));
    setWzNotes("");
    setWizardStep(1);
    setWizardOpen(true);
    loadTechnicians();
    loadOwnServices();
  };

  const handleDestTypeChange = (val) => {
    setWzDestType(val);
    setWzDestTechId("");
    setWzDestServiceId("");
    setDestServices([]);
  };

  const handleDestTechChange = (techId) => {
    setWzDestTechId(techId);
    setWzDestServiceId("");
    loadDestServices(techId);
  };

  const canProceedStep = () => {
    switch (wizardStep) {
      case 1: return wzSource && wzQty && parseFloat(wzQty) > 0 && parseFloat(wzQty) <= wzSource.quantity_available;
      case 2: return wzDestType !== "";
      case 3:
        if (wzDestType === "own") return true; // service or extra is fine
        return wzDestTechId !== "";
      case 4: return wzDate !== "";
      default: return true;
    }
  };

  const submitTransfer = async () => {
    setSubmitting(true);
    try {
      const isInternal = wzDestType === "own";
      // For own transfers, get own tech ID from supplies
      let myTechId = "";
      if (wzSource) {
        myTechId = wzSource.technician_id;
      }

      const body = {
        from_supply_id: wzSource.id,
        to_technician_id: isInternal ? myTechId : wzDestTechId,
        to_service_id: (wzDestServiceId && wzDestServiceId !== "__extra__") ? wzDestServiceId : null,
        quantity: parseFloat(wzQty),
        transfer_date: wzDate,
        notes: wzNotes || null,
      };

      const endpoint = isInternal ? "/supplies/internal-transfer" : "/supplies/transfer";
      await api.post(endpoint, body);
      toast.success(isInternal ? "Traslado interno realizado" : "Solicitud de traslado enviada");
      setWizardOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al realizar traslado");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Accept / Reject ─────────────────────────
  const handleAccept = async (transferId) => {
    try {
      await api.post(`/supplies/transfers/${transferId}/accept`);
      toast.success("Traslado aceptado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const handleReject = async (transferId) => {
    try {
      await api.post(`/supplies/transfers/${transferId}/reject`);
      toast.success("Traslado rechazado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  // ─── Flatten all supplies for source selection ──
  const allSupplies = [
    ...groups.flatMap(g => g.supplies.map(s => ({
      ...s,
      _label: `${s.product_name} — ${g.client_name} (${g.service_time})`,
      _serviceLabel: `${g.client_name} (${g.order_number || g.service_time})`,
    }))),
    ...extras.map(e => ({
      ...e,
      _label: `${e.product_name} — Extra`,
      _serviceLabel: "Extra",
    })),
  ].filter(s => s.quantity_available > 0);

  // Status badge helper
  const statusBadge = (status) => {
    if (status === "pending") return <Badge className="bg-amber-100 text-amber-800 border-amber-300"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
    if (status === "accepted") return <Badge className="bg-green-100 text-green-800 border-green-300"><Check className="w-3 h-3 mr-1" />Aceptado</Badge>;
    if (status === "rejected") return <Badge className="bg-red-100 text-red-800 border-red-300"><X className="w-3 h-3 mr-1" />Rechazado</Badge>;
    return null;
  };

  const tabs = [
    { id: "inventory", label: "Inventario", icon: Package },
    { id: "pending", label: "Pendientes", icon: Inbox, badge: pendingCount },
    { id: "history", label: "Historial", icon: History },
  ];

  return (
    <div className="px-4 pt-4 pb-6" data-testid="tech-supplies-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => nav("/tech")} className="p-2 -ml-2 rounded-lg hover:bg-secondary" data-testid="supplies-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
            Gestión de Insumos
          </h1>
          <p className="text-xs text-muted-foreground">Administra y traslada tus productos</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-secondary/50 rounded-xl p-1" data-testid="supplies-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? "bg-white shadow-sm text-black" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`supplies-tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{t.label}</span>
            {t.badge > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-24 rounded-xl bg-secondary animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* ════════ INVENTORY TAB ════════ */}
          {tab === "inventory" && (
            <div className="space-y-4" data-testid="supplies-inventory">
              {/* New Transfer button */}
              {allSupplies.length > 0 && (
                <Button
                  onClick={() => openWizard(null)}
                  className="w-full h-12 bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold"
                  data-testid="new-transfer-btn"
                >
                  <ArrowRightLeft className="w-5 h-5 mr-2" />
                  Nuevo Traslado de Insumos
                </Button>
              )}

              {/* Per-service groups */}
              {groups.map(g => (
                <Card key={g.service_id} className="p-4" data-testid={`supply-group-${g.service_id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">Tarea</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{g.client_name}</div>
                      <div className="text-xs text-muted-foreground">{g.service_date} · {g.service_time} {g.order_number && `· ${g.order_number}`}</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {g.supplies.map(s => (
                      <div key={s.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-secondary/50" data-testid={`supply-item-${s.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{s.product_name}</div>
                          <div className="text-xs text-muted-foreground">
                            Asignado: {s.quantity_assigned} {s.unit} · Disponible: <span className={s.quantity_available < s.quantity_assigned ? "text-amber-600 font-semibold" : "font-semibold"}>{s.quantity_available} {s.unit}</span>
                          </div>
                        </div>
                        {s.quantity_available > 0 && (
                          <Button size="sm" variant="ghost" onClick={() => openWizard(s)} className="shrink-0 ml-2" data-testid={`transfer-btn-${s.id}`}>
                            <ArrowRightLeft className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              ))}

              {/* Extras */}
              {extras.length > 0 && (
                <Card className="p-4" data-testid="supply-group-extras">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200">Extra</Badge>
                    <div className="text-sm font-semibold">Insumos Extra</div>
                  </div>
                  <div className="space-y-2">
                    {extras.map(s => (
                      <div key={s.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-secondary/50" data-testid={`supply-item-${s.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{s.product_name}</div>
                          <div className="text-xs text-muted-foreground">
                            Asignado: {s.quantity_assigned} {s.unit} · Disponible: <span className="font-semibold">{s.quantity_available} {s.unit}</span>
                          </div>
                        </div>
                        {s.quantity_available > 0 && (
                          <Button size="sm" variant="ghost" onClick={() => openWizard(s)} className="shrink-0 ml-2" data-testid={`transfer-btn-${s.id}`}>
                            <ArrowRightLeft className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {groups.length === 0 && extras.length === 0 && (
                <Card className="p-8 text-center">
                  <PackageOpen className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No tienes insumos asignados</p>
                </Card>
              )}
            </div>
          )}

          {/* ════════ PENDING TAB ════════ */}
          {tab === "pending" && (
            <div className="space-y-3" data-testid="supplies-pending">
              {pending.length === 0 ? (
                <Card className="p-8 text-center">
                  <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No tienes traslados pendientes de aprobación</p>
                </Card>
              ) : pending.map(t => (
                <Card key={t.id} className="p-4" data-testid={`pending-transfer-${t.id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                      <Send className="w-4 h-4 text-amber-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{t.product_name}</div>
                      <div className="text-xs text-muted-foreground">
                        De: <span className="font-medium text-foreground">{t.from_technician_name}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Cantidad: <span className="font-semibold text-foreground">{t.quantity} {t.unit}</span>
                        {" · "}Origen: <Badge variant="outline" className="text-[10px] px-1.5 py-0">{t.from_origin === "tarea" ? "Tarea" : "Extra"}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Fecha traslado: {t.transfer_date}
                      </div>
                      {t.notes && <div className="text-xs text-muted-foreground mt-1 italic">"{t.notes}"</div>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAccept(t.id)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`accept-transfer-${t.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" /> Aceptar
                    </Button>
                    <Button
                      onClick={() => handleReject(t.id)}
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`reject-transfer-${t.id}`}
                    >
                      <X className="w-4 h-4 mr-1" /> Rechazar
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* ════════ HISTORY TAB ════════ */}
          {tab === "history" && (
            <div className="space-y-3" data-testid="supplies-history">
              {history.length === 0 ? (
                <Card className="p-8 text-center">
                  <History className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">Sin historial de traslados</p>
                </Card>
              ) : history.map(t => (
                <Card key={t.id} className="p-3" data-testid={`history-transfer-${t.id}`}>
                  <div className="flex items-start gap-2.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      t.status === "accepted" ? "bg-green-100" : t.status === "rejected" ? "bg-red-100" : "bg-amber-100"
                    }`}>
                      <ArrowRightLeft className={`w-3.5 h-3.5 ${
                        t.status === "accepted" ? "text-green-700" : t.status === "rejected" ? "text-red-700" : "text-amber-700"
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{t.product_name}</span>
                        {statusBadge(t.status)}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        <span className="font-medium">{t.quantity} {t.unit}</span>
                        {" · "}
                        {t.from_technician_name} → {t.to_technician_name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t.from_origin === "tarea" ? "Tarea" : "Extra"} → {t.to_origin === "tarea" ? "Tarea" : "Extra"}
                        {" · "}{t.transfer_date}
                      </div>
                      {t.notes && <div className="text-xs text-muted-foreground italic mt-0.5">"{t.notes}"</div>}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* ════════ TRANSFER WIZARD DIALOG ════════ */}
      <Dialog open={wizardOpen} onOpenChange={(o) => { if (!o) setWizardOpen(false); }}>
        <DialogContent className="max-w-md mx-auto" data-testid="transfer-wizard">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-[#FFD62B]" />
              Trasladar Insumo
              <Badge variant="outline" className="ml-auto">Paso {wizardStep}/5</Badge>
            </DialogTitle>
            <DialogDescription>
              {wizardStep === 1 && "Selecciona el producto y cantidad a trasladar"}
              {wizardStep === 2 && "¿A quién va dirigido el traslado?"}
              {wizardStep === 3 && "Selecciona el destino específico"}
              {wizardStep === 4 && "Fecha y notas del traslado"}
              {wizardStep === 5 && "Confirma los datos del traslado"}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-4 min-h-[200px]">
            {/* ── Step 1: Source + Qty ── */}
            {wizardStep === 1 && (
              <>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block">Producto origen</Label>
                  <Select
                    value={wzSource?.id || ""}
                    onValueChange={(val) => {
                      const s = allSupplies.find(x => x.id === val);
                      setWzSource(s || null);
                      setWzQty("");
                    }}
                  >
                    <SelectTrigger className="h-11" data-testid="wizard-source-select">
                      <SelectValue placeholder="Seleccionar producto..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allSupplies.map(s => (
                        <SelectItem key={s.id} value={s.id} data-testid={`wizard-source-option-${s.id}`}>
                          <span className="truncate">{s._label} ({s.quantity_available} {s.unit})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {wzSource && (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block">
                      Cantidad a trasladar <span className="text-muted-foreground font-normal">(máx: {wzSource.quantity_available} {wzSource.unit})</span>
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0.1"
                      max={wzSource.quantity_available}
                      value={wzQty}
                      onChange={e => setWzQty(e.target.value)}
                      placeholder="0"
                      className="h-11"
                      data-testid="wizard-qty-input"
                    />
                    {wzQty && parseFloat(wzQty) > wzSource.quantity_available && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Excede la cantidad disponible
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── Step 2: Destination type ── */}
            {wizardStep === 2 && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider mb-3 block">Tipo de traslado</Label>
                <div className="space-y-2">
                  <button
                    onClick={() => handleDestTypeChange("own")}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      wzDestType === "own" ? "border-[#FFD62B] bg-[#FFD62B]/10" : "border-border hover:border-muted-foreground/30"
                    }`}
                    data-testid="wizard-dest-own"
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <Package className="w-5 h-5 text-blue-700" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm">Entre mis servicios</div>
                      <div className="text-xs text-muted-foreground">Mover entre mis propias tareas o extra</div>
                    </div>
                    {wzDestType === "own" && <Check className="w-5 h-5 text-[#FFD62B] ml-auto shrink-0" />}
                  </button>
                  <button
                    onClick={() => handleDestTypeChange("other")}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      wzDestType === "other" ? "border-[#FFD62B] bg-[#FFD62B]/10" : "border-border hover:border-muted-foreground/30"
                    }`}
                    data-testid="wizard-dest-other"
                  >
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                      <User className="w-5 h-5 text-green-700" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm">A otro técnico</div>
                      <div className="text-xs text-muted-foreground">Enviar a servicio o extra de otro técnico</div>
                    </div>
                    {wzDestType === "other" && <Check className="w-5 h-5 text-[#FFD62B] ml-auto shrink-0" />}
                  </button>
                </div>
              </div>
            )}

            {/* ── Step 3: Destination details ── */}
            {wizardStep === 3 && (
              <>
                {wzDestType === "own" && (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block">Servicio destino</Label>
                    <Select value={wzDestServiceId} onValueChange={setWzDestServiceId}>
                      <SelectTrigger className="h-11" data-testid="wizard-own-service-select">
                        <SelectValue placeholder="Extra (sin servicio)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__extra__" data-testid="wizard-own-extra-option">
                          Insumos Extra (sin servicio)
                        </SelectItem>
                        {ownServices
                          .filter(os => os.id !== wzSource?.service_id)
                          .map(os => (
                            <SelectItem key={os.id} value={os.id} data-testid={`wizard-own-svc-${os.id}`}>
                              {os.client_name} · {os.scheduled_time} ({os.scheduled_date})
                            </SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-2">
                      Traslados internos se ejecutan de inmediato.
                    </p>
                  </div>
                )}
                {wzDestType === "other" && (
                  <>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block">Técnico destino</Label>
                      <Select value={wzDestTechId} onValueChange={handleDestTechChange}>
                        <SelectTrigger className="h-11" data-testid="wizard-tech-select">
                          <SelectValue placeholder="Seleccionar técnico..." />
                        </SelectTrigger>
                        <SelectContent>
                          {technicians.map(t => (
                            <SelectItem key={t.id} value={t.id} data-testid={`wizard-tech-option-${t.id}`}>
                              {t.full_name} ({t.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {wzDestTechId && (
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block">Servicio destino</Label>
                        <Select value={wzDestServiceId} onValueChange={setWzDestServiceId}>
                          <SelectTrigger className="h-11" data-testid="wizard-dest-service-select">
                            <SelectValue placeholder="Extra del técnico" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__extra__" data-testid="wizard-dest-extra-option">
                              Extra del técnico
                            </SelectItem>
                            {destServices.map(ds => (
                              <SelectItem key={ds.id} value={ds.id} data-testid={`wizard-dest-svc-${ds.id}`}>
                                {ds.client_name} · {ds.scheduled_time} ({ds.scheduled_date})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                      <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                      El técnico destino deberá aceptar el traslado para que se haga efectivo.
                    </div>
                  </>
                )}
              </>
            )}

            {/* ── Step 4: Date & Notes ── */}
            {wizardStep === 4 && (
              <>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block">Fecha del traslado</Label>
                  <Input
                    type="date"
                    value={wzDate}
                    onChange={e => setWzDate(e.target.value)}
                    className="h-11"
                    data-testid="wizard-date-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block">Notas (opcional)</Label>
                  <Textarea
                    value={wzNotes}
                    onChange={e => setWzNotes(e.target.value)}
                    placeholder="Motivo del traslado, observaciones..."
                    rows={3}
                    data-testid="wizard-notes-input"
                  />
                </div>
              </>
            )}

            {/* ── Step 5: Confirmation ── */}
            {wizardStep === 5 && (
              <div className="space-y-3">
                <div className="rounded-xl border p-4 space-y-2">
                  <ConfirmRow label="Producto" value={wzSource?.product_name} />
                  <ConfirmRow label="Cantidad" value={`${wzQty} ${wzSource?.unit}`} />
                  <ConfirmRow label="Origen" value={wzSource?._serviceLabel || (wzSource?.origin === "extra" ? "Extra" : "Tarea")} />
                  <ConfirmRow label="Destino" value={
                    wzDestType === "own"
                      ? (wzDestServiceId && wzDestServiceId !== "__extra__"
                          ? ownServices.find(s => s.id === wzDestServiceId)?.client_name + " (" + ownServices.find(s => s.id === wzDestServiceId)?.scheduled_time + ")"
                          : "Mis Insumos Extra")
                      : (technicians.find(t => t.id === wzDestTechId)?.full_name || "—") +
                        (wzDestServiceId && wzDestServiceId !== "__extra__"
                          ? ` · ${destServices.find(s => s.id === wzDestServiceId)?.client_name || "Servicio"}`
                          : " · Extra")
                  } />
                  <ConfirmRow label="Fecha" value={wzDate} />
                  {wzNotes && <ConfirmRow label="Notas" value={wzNotes} />}
                </div>
                {wzDestType === "other" && (
                  <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900">
                    Este traslado quedará pendiente hasta que el técnico destino lo acepte.
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex-row gap-2">
            {wizardStep > 1 && (
              <Button
                variant="outline"
                onClick={() => setWizardStep(s => s - 1)}
                className="flex-1"
                data-testid="wizard-back-btn"
              >
                Atrás
              </Button>
            )}
            {wizardStep < 5 ? (
              <Button
                onClick={() => setWizardStep(s => s + 1)}
                disabled={!canProceedStep()}
                className="flex-1 bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold"
                data-testid="wizard-next-btn"
              >
                Siguiente <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={submitTransfer}
                disabled={submitting}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
                data-testid="wizard-confirm-btn"
              >
                {submitting ? "Enviando..." : (wzDestType === "own" ? "Trasladar" : "Enviar solicitud")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConfirmRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-2 text-sm">
      <span className="text-muted-foreground shrink-0">{label}:</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
